import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import './Table.css';
import { 
  ContextMenu, 
  FilterIcon 
} from './FilterComponents.js';
import { filterAndSortTableData, columnFilterHasValue, isColumnFilterActive } from './tableDataUtils.js';
import { loadFromSessionStorage, saveToSessionStorage } from './tableSession.js';
import TableFilterPopover from './TableFilterPopover.js';
import { TableSelectionHeader, TableSelectionCell } from './TableSelection.js';
import TableBulkDock from './TableBulkDock.js';
import TablePagination from './TablePagination.js';

/**
 * Session storage utilities live in tableSession.js.
 * useTableState is re-exported so existing imports from this module keep working.
 */
export { useTableState } from './useTableState.js';

/** Default column width when no value is stored (before or without resize). */
const DEFAULT_COLUMN_WIDTH_PX = 200;

/**
 * Whether two row arrays hold the same rows. Filtering and sorting reuse the row objects from
 * `data`, so reference equality per element is enough to tell "same rows, new array" from a real
 * change, without needing a row key.
 */
const haveSameRows = (previous, next) => {
  if (previous === next) return true;
  if (!previous || previous.length !== next.length) return false;
  return previous.every((row, index) => row === next[index]);
};

/**
 * Reusable Table component with sorting capabilities
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Array of Column instances defining the table structure
 * @param {string} [props.id] - Unique identifier for session storage persistence
 * @param {string} [props.className] - Additional CSS class for the table
 * @param {Object} [props.tableProps] - Additional props for the table element
 * @param {Function} [props.onRowClick] - Callback when a row is clicked
 * @param {Function} [props.onRowContextMenu] - Callback when a row is right-clicked (row, index, event). Call event.preventDefault() to suppress the native menu.
 * @param {Function} [props.getRowKey] - Function to get unique key for each row (defaults to index)
 * @param {React.ReactNode} [props.emptyMessage] - Content to show when no data
 * @param {boolean} [props.enableSelection] - Enable row selection functionality
 * @param {Array} [props.bulkActions] - Array of bulk action objects with {key, label, icon, onClick}
 * @param {Set} [props.selectedRows] - External selected rows state
 * @param {Function} [props.onSelectionChange] - Callback when selection changes
 * @param {Set} [props.highlightedRows] - External highlighted rows state
 * @param {Function} [props.onHighlightChange] - Callback when highlighting changes
 * @param {Object} [props.paginationState] - External pagination state
 * @param {Function} [props.onPaginationChange] - Callback when pagination changes
 * @param {*} [props.ensureRowVisibleKey] - Once the filtered list is non-empty, show the page containing this row key. Missing keys leave the current page. Consumed after that first search so later updates do not steal the page.
 * @param {Function} [props.onCurrentPageRowsChange] - Callback when current page rows change
 * @param {Function} [props.onFilteredSortedDataChange] - Callback when filtered/sorted data changes (full unpaginated list)
 * @param {Array} [props.columnOrder] - Order of column keys (for customization)
 * @param {Set} [props.hiddenColumns] - Set of hidden column keys (for customization)
 * @param {React.ReactNode} [props.settingsButton] - Optional settings button to render next to Select All
 */
const Table = ({
  data = [],
  columns = [],
  id,
  className = '',
  tableProps = {},
  onRowClick,
  onRowContextMenu,
  getRowKey = (row, index) => index,
  emptyMessage = null,
  sortState: externalSortState,
  onSortChange,
  filterValues: externalFilterValues,
  onFilterChange,
  disabledFilters: externalDisabledFilters,
  onDisabledFiltersChange,
  enableSelection = false,
  bulkActions = [],
  selectedRows: externalSelectedRows,
  onSelectionChange,
  highlightedRows: externalHighlightedRows,
  onHighlightChange,
  paginationState: externalPaginationState,
  onPaginationChange,
  ensureRowVisibleKey,
  onCurrentPageRowsChange,
  onFilteredSortedDataChange,
  columnOrder,
  hiddenColumns,
  settingsButton
}) => {
  const [internalSortState, setInternalSortState] = useState(() => 
    loadFromSessionStorage(id, 'sortState', {
      columnKey: null,
      direction: null // null, 'asc', 'desc'
    })
  );

  // Process columns based on columnOrder and hiddenColumns for customization
  const processedColumns = useMemo(() => {
    console.log('=== Table processedColumns ===');
    console.log('columnOrder:', columnOrder);
    console.log('hiddenColumns:', hiddenColumns ? Array.from(hiddenColumns) : 'none');
    
    // No custom order - return all columns (minus hidden)
    if (!columnOrder || columnOrder.length === 0) {
      if (hiddenColumns && hiddenColumns.size > 0) {
        return columns.filter(col => !hiddenColumns.has(col.key));
      }
      return columns;
    }

    // Build ordered columns
    const columnMap = new Map(columns.map(col => [col.key, col]));
    const result = [];
    
    for (const key of columnOrder) {
      const col = columnMap.get(key);
      const isHidden = hiddenColumns && hiddenColumns.has(key);
      if (col && !isHidden) {
        result.push(col);
      }
    }
    
    // Add any new columns not in order
    for (const col of columns) {
      if (!columnOrder.includes(col.key)) {
        const isHidden = hiddenColumns && hiddenColumns.has(col.key);
        if (!isHidden) {
          result.push(col);
        }
      }
    }
    
    console.log('result:', result.map(c => c.key));
    return result;
  }, [columns, columnOrder, hiddenColumns]);

  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    column: null
  });

  // Filter state - uses columnKey instead of column reference to avoid stale references
  const [activeFilter, setActiveFilter] = useState({
    isOpen: false,
    columnKey: null,
    type: null
  });

  // Filter values state - tracks all active filters
  const [internalFilterValues, setInternalFilterValues] = useState(() => 
    loadFromSessionStorage(id, 'filterValues', {})
  );
  
  // Disabled filters state - tracks which filters are disabled but retain values
  const [internalDisabledFilters, setInternalDisabledFilters] = useState(() => 
    loadFromSessionStorage(id, 'disabledFilters', new Set())
  );

  // Pagination state
  const [internalPaginationState, setInternalPaginationState] = useState(() => 
    loadFromSessionStorage(id, 'paginationState', {
      currentPage: 1,
      pageSize: 20
    })
  );

  // Selection state
  const [internalSelectedRows, setInternalSelectedRows] = useState(new Set());
  
  // Highlighting state
  const [internalHighlightedRows, setInternalHighlightedRows] = useState(() => 
    loadFromSessionStorage(id, 'highlightedRows', new Set())
  );

  // Column widths state - maps column key to width in pixels
  const [columnWidths, setColumnWidths] = useState(() => 
    loadFromSessionStorage(id, 'columnWidths', {})
  );

  // Column resize state
  const [resizeState, setResizeState] = useState({
    isResizing: false,
    columnKey: null,
    startX: 0,
    startWidth: 0,
    currentX: 0
  });

  // Ref for table scroll wrapper to calculate relative positions
  const tableScrollRef = useRef(null);
  const filterHeaderRefs = useRef({});
  
  // Use external selection state if provided, otherwise use internal state
  const selectedRows = externalSelectedRows !== undefined ? externalSelectedRows : internalSelectedRows;
  const setSelectedRows = onSelectionChange ? 
    (newSelection) => onSelectionChange(newSelection) : 
    setInternalSelectedRows;

  // Use external highlighting state if provided, otherwise use internal state
  const highlightedRows = externalHighlightedRows !== undefined ? externalHighlightedRows : internalHighlightedRows;
  const setHighlightedRows = onHighlightChange ? 
    (newHighlighting) => onHighlightChange(newHighlighting) : 
    setInternalHighlightedRows;

  // Use external sort state if provided, otherwise use internal state
  const sortState = externalSortState !== undefined ? externalSortState : internalSortState;
  const setSortState = onSortChange || setInternalSortState;

  // Use external filter state if provided, otherwise use internal state
  const filterValues = externalFilterValues !== undefined ? externalFilterValues : internalFilterValues;
  const setFilterValues = onFilterChange || setInternalFilterValues;
  const disabledFilters = externalDisabledFilters !== undefined ? externalDisabledFilters : internalDisabledFilters;
  const setDisabledFilters = onDisabledFiltersChange || setInternalDisabledFilters;

  // Use external pagination state if provided, otherwise use internal state
  const paginationState = externalPaginationState !== undefined ? externalPaginationState : internalPaginationState;
  const setPaginationState = onPaginationChange || setInternalPaginationState;

  // Close filter when clicking outside
  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (!event.target.closest('.filter-input') && 
          !event.target.closest('.filter-icon') &&
          !event.target.closest('.context-menu')) {
        setActiveFilter({ isOpen: false, columnKey: null, type: null });
      }
      
      if (!event.target.closest('.context-menu')) {
        setContextMenu({ isOpen: false, x: 0, y: 0, column: null });
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  // Save sort state to session storage when it changes
  useEffect(() => {
    const currentSortState = externalSortState !== undefined ? externalSortState : internalSortState;
    saveToSessionStorage(id, 'sortState', currentSortState);
  }, [id, internalSortState, externalSortState]);

  // Save filter values to session storage when they change
  useEffect(() => {
    const currentFilterValues = externalFilterValues !== undefined ? externalFilterValues : internalFilterValues;
    saveToSessionStorage(id, 'filterValues', currentFilterValues);
  }, [id, internalFilterValues, externalFilterValues]);

  // Save disabled filters to session storage when they change
  useEffect(() => {
    const currentDisabledFilters = externalDisabledFilters !== undefined ? externalDisabledFilters : internalDisabledFilters;
    saveToSessionStorage(id, 'disabledFilters', currentDisabledFilters);
  }, [id, internalDisabledFilters, externalDisabledFilters]);

  // Save pagination state to session storage when it changes
  useEffect(() => {
    const currentPaginationState = externalPaginationState !== undefined ? externalPaginationState : internalPaginationState;
    saveToSessionStorage(id, 'paginationState', currentPaginationState);
  }, [id, internalPaginationState, externalPaginationState]);

  // Save highlighted rows to session storage when they change
  useEffect(() => {
    const currentHighlightedRows = externalHighlightedRows !== undefined ? externalHighlightedRows : internalHighlightedRows;
    saveToSessionStorage(id, 'highlightedRows', currentHighlightedRows);
  }, [id, internalHighlightedRows, externalHighlightedRows]);

  // Save column widths to session storage when they change
  useEffect(() => {
    saveToSessionStorage(id, 'columnWidths', columnWidths);
  }, [id, columnWidths]);

  // Track if changes are user-initiated to reset pagination appropriately
  const userInitiatedChange = useRef(false);
  
  // Reset to page 1 only when user changes filters or sorting
  useEffect(() => {
    if (userInitiatedChange.current) {
      setPaginationState(prev => ({
        ...prev,
        currentPage: 1
      }));
      userInitiatedChange.current = false;
    }
  }, [filterValues, sortState, setPaginationState]);

  // Filter and sort the data (without pagination)
  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(data)) {
      return data;
    }

    return filterAndSortTableData(data, processedColumns, {
      filterValues,
      disabledFilters,
      sortState,
    });
  }, [data, processedColumns, sortState, filterValues, disabledFilters]);

  // One-shot page reveal: wait for a non-empty filtered list, then search by key.
  // The empty first paint must not consume the key; a missing key must not invent a page.
  const [consumedEnsureKey, setConsumedEnsureKey] = useState(null);
  const ensureResult = useMemo(() => {
    if (!ensureRowVisibleKey || consumedEnsureKey === ensureRowVisibleKey) {
      return { ready: false, page: null };
    }
    if (!Array.isArray(filteredAndSortedData) || filteredAndSortedData.length === 0) {
      return { ready: false, page: null };
    }
    const index = filteredAndSortedData.findIndex((row, rowIndex) => getRowKey(row, rowIndex) === ensureRowVisibleKey);
    if (index === -1) {
      return { ready: true, page: null };
    }
    const pageSize = paginationState.pageSize || 20;
    return { ready: true, page: Math.floor(index / pageSize) + 1 };
  }, [ensureRowVisibleKey, consumedEnsureKey, filteredAndSortedData, paginationState.pageSize, getRowKey]);

  const effectiveCurrentPage = ensureResult.page ?? paginationState.currentPage;

  // Apply pagination to the filtered and sorted data
  const processedData = useMemo(() => {
    if (!Array.isArray(filteredAndSortedData)) {
      return filteredAndSortedData;
    }

    const { pageSize } = paginationState;
    const startIndex = (effectiveCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    
    return filteredAndSortedData.slice(startIndex, endIndex);
  }, [filteredAndSortedData, paginationState, effectiveCurrentPage]);

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    if (!Array.isArray(filteredAndSortedData)) {
      return { totalItems: 0, totalPages: 0, currentPage: 1, pageSize: 20 };
    }

    const totalItems = filteredAndSortedData.length;
    const { pageSize } = paginationState;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      totalItems,
      totalPages,
      currentPage: Math.min(effectiveCurrentPage, Math.max(1, totalPages)), // Ensure current page is valid
      pageSize,
      startItem: totalItems === 0 ? 0 : (effectiveCurrentPage - 1) * pageSize + 1,
      endItem: Math.min(effectiveCurrentPage * pageSize, totalItems)
    };
  }, [filteredAndSortedData, paginationState, effectiveCurrentPage]);

  // Persist the revealed page and consume the key so later list updates do not steal the page.
  useEffect(() => {
    if (!ensureResult.ready || !ensureRowVisibleKey) return;
    if (ensureResult.page != null && ensureResult.page !== paginationState.currentPage) {
      setPaginationState(prev => ({
        ...prev,
        currentPage: ensureResult.page
      }));
    }
    setConsumedEnsureKey(ensureRowVisibleKey);
  }, [ensureResult.ready, ensureResult.page, ensureRowVisibleKey, paginationState.currentPage, setPaginationState]);

  // Reset to last page if current page is out of bounds
  useEffect(() => {
    if (ensureResult.page != null) return;
    if (paginationState.currentPage > paginationInfo.totalPages && paginationInfo.totalPages > 0) {
      setPaginationState(prev => ({
        ...prev,
        currentPage: paginationInfo.totalPages
      }));
    }
  }, [paginationInfo.totalPages, paginationState.currentPage, setPaginationState, ensureResult.page]);

  // `processedData` is a fresh array on any column, sort, filter or pagination recompute, even
  // when it holds exactly the same rows. Parents use these callbacks to fetch per-row data, so
  // firing on array identity turned an unrelated re-render into a burst of requests. Compare
  // contents instead: a genuinely new or replaced row still differs by reference.
  const notifiedPageRowsRef = useRef(null);
  useEffect(() => {
    if (!onCurrentPageRowsChange || !Array.isArray(processedData)) return;
    if (haveSameRows(notifiedPageRowsRef.current, processedData)) return;

    notifiedPageRowsRef.current = processedData;
    onCurrentPageRowsChange(processedData);
  }, [processedData, onCurrentPageRowsChange]);

  // Notify parent component when filtered/sorted data changes (full unpaginated list)
  const notifiedFilteredRowsRef = useRef(null);
  useEffect(() => {
    if (!onFilteredSortedDataChange || !Array.isArray(filteredAndSortedData)) return;
    if (haveSameRows(notifiedFilteredRowsRef.current, filteredAndSortedData)) return;

    notifiedFilteredRowsRef.current = filteredAndSortedData;
    onFilteredSortedDataChange(filteredAndSortedData);
  }, [filteredAndSortedData, onFilteredSortedDataChange]);

  /**
   * Handle column header click for sorting
   * @param {Column} column - The column that was clicked
   */
  const handleHeaderClick = (column, event) => {
    // Prevent default sorting if right-clicking, clicking filter icon, or clicking within filter components
    if (event.button === 2 || 
        event.target.closest('.filter-icon') ||
        event.target.closest('.filter-input') ||
        event.target.closest('.context-menu')) {
      return;
    }

    if (!column.sortable) return;

    userInitiatedChange.current = true;
    setSortState(prevState => {
      // If clicking the same column, cycle through: null -> asc -> desc -> null
      if (prevState.columnKey === column.key) {
        if (prevState.direction === null) {
          return { columnKey: column.key, direction: 'asc' };
        } else if (prevState.direction === 'asc') {
          return { columnKey: column.key, direction: 'desc' };
        } else {
          return { columnKey: null, direction: null };
        }
      } else {
        // Clicking a different column, start with ascending
        return { columnKey: column.key, direction: 'asc' };
      }
    });
  };

  /**
   * Handle right-click on column header to show context menu
   */
  const handleHeaderRightClick = (event, column) => {
    event.preventDefault();
    setContextMenu({
      isOpen: true,
      x: event.clientX,
      y: event.clientY,
      column
    });
    // Close any open filter
    setActiveFilter({ isOpen: false, columnKey: null, type: null });
  };

  /**
   * Close context menu
   */
  const closeContextMenu = () => {
    setContextMenu({ isOpen: false, x: 0, y: 0, column: null });
  };

  /**
   * Handle sort from context menu
   */
  const handleContextSort = (direction) => {
    if (contextMenu.column && contextMenu.column.sortable) {
      userInitiatedChange.current = true;
      setSortState({
        columnKey: contextMenu.column.key,
        direction: direction
      });
    }
  };

  /**
   * Handle filter from context menu
   */
  const handleContextFilter = () => {
    if (contextMenu.column && contextMenu.column.filterable) {
      setActiveFilter({
        isOpen: true,
        columnKey: contextMenu.column.key,
        type: contextMenu.column.dataType
      });
    }
  };

  /**
   * Check if a column has a filter value (regardless of enabled/disabled state)
   */
  const hasFilterValue = (column) => columnFilterHasValue(column, filterValues[column.key]);

  /**
   * Check if a column has an active (enabled) filter
   */
  const hasActiveFilter = (column) => isColumnFilterActive(column, filterValues[column.key], disabledFilters);

  /**
   * Check if a column has a disabled filter
   */
  const hasDisabledFilter = (column) => {
    return hasFilterValue(column) && disabledFilters.has(column.key);
  };

  /**
   * Handle filter icon click - implements three-state logic
   */
  const handleFilterIconClick = (event, column) => {
    event.stopPropagation();
    event.preventDefault();
    
    if (!hasFilterValue(column)) {
      // State A: No filter -> Open filter input to create one (A -> C)
      setActiveFilter({
        isOpen: true,
        columnKey: column.key,
        type: column.dataType
      });
    } else if (hasActiveFilter(column)) {
      // State C: Filter enabled -> Disable but keep values (C -> B)
      setDisabledFilters(prev => new Set([...prev, column.key]));
    } else {
      // State B: Filter disabled -> Enable it (B -> C)
      setDisabledFilters(prev => {
        const newSet = new Set(prev);
        newSet.delete(column.key);
        return newSet;
      });
    }
    closeContextMenu();
  };

  /**
   * Close filter input
   */
  const closeFilter = () => {
    setActiveFilter({ isOpen: false, columnKey: null, type: null });
  };

  /**
   * Handle filter value change
   */
  const handleFilterChange = (value) => {
    if (activeFilter.columnKey) {
      userInitiatedChange.current = true;
      const columnKey = activeFilter.columnKey;
      
      // Check if value is meaningful
      const hasValue = (() => {
        if (activeFilter.type === 'enum') {
          return value && Object.values(value).some(Boolean);
        }
        if (activeFilter.type === 'date') {
          return value && (value.startDate || value.endDate);
        }
        return value != null && String(value).trim() !== '';
      })();

      setFilterValues(prev => {
        if (hasValue) {
          return { ...prev, [columnKey]: value };
        } else {
          const { [columnKey]: removed, ...rest } = prev;
          return rest;
        }
      });

      // If removing the filter value completely, also remove from disabled filters
      if (!hasValue) {
        setDisabledFilters(prev => {
          const newSet = new Set(prev);
          newSet.delete(columnKey);
          return newSet;
        });
      }
    }
  };

  /**
   * Get the sort indicator for a column header
   * @param {Column} column - The column to get indicator for
   * @returns {React.ReactNode} - The sort indicator component
   */
  const getSortIndicator = (column) => {
    if (!column.sortable) return null;

    const isActive = sortState.columnKey === column.key;
    const direction = isActive ? sortState.direction : null;

    let icon = faChevronDown;
    let className = 'sort-indicator';

    if (direction === 'desc') {
      icon = faChevronUp;
      className += ' sort-indicator-active';
    } else if (direction === 'asc') {
      className += ' sort-indicator-active';
    } else {
      className += ' sort-indicator-inactive';
    }

    return (
      <FontAwesomeIcon 
        icon={icon} 
        className={className}
      />
    );
  };

  /**
   * Handle selection toggle for a single row
   */
  const handleRowSelection = (row, event) => {
    event.stopPropagation();
    const rowKey = getRowKey(row);
    
    setSelectedRows(prevSelected => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(rowKey)) {
        newSelected.delete(rowKey);
      } else {
        newSelected.add(rowKey);
      }
      return newSelected;
    });
  };

  /**
   * Handle select all toggle
   */
  const handleSelectAll = (event) => {
    event.stopPropagation();
    
    setSelectedRows(prevSelected => {
      const currentPageRowKeys = new Set(processedData.map(row => getRowKey(row)));
      const allCurrentPageSelected = [...currentPageRowKeys].every(key => prevSelected.has(key));
      
      if (allCurrentPageSelected) {
        // Deselect all rows on current page
        const newSelected = new Set(prevSelected);
        currentPageRowKeys.forEach(key => newSelected.delete(key));
        return newSelected;
      } else {
        // Select all rows on current page
        const newSelected = new Set(prevSelected);
        currentPageRowKeys.forEach(key => newSelected.add(key));
        return newSelected;
      }
    });
  };

  /**
   * Handle select all across all pages toggle
   */
  const handleSelectAllPages = () => {
    if (!Array.isArray(filteredAndSortedData)) return;
    
    setSelectedRows(prevSelected => {
      const allRowKeys = new Set(filteredAndSortedData.map(row => getRowKey(row)));
      const allSelected = [...allRowKeys].every(key => prevSelected.has(key));
      
      if (allSelected) {
        // Deselect all rows across all pages
        const newSelected = new Set(prevSelected);
        allRowKeys.forEach(key => newSelected.delete(key));
        return newSelected;
      } else {
        // Select all rows across all pages
        return new Set([...prevSelected, ...allRowKeys]);
      }
    });
  };

  /**
   * Handle row click
   * @param {Object} row - The row data
   * @param {number} index - The row index
   * @param {Event} event - The click event
   */
  const handleRowClick = (row, index, event) => {
    if (onRowClick) {
      onRowClick(row, index, event);
    }
  };

  /**
   * Handle row right-click (context menu)
   */
  const handleRowContextMenu = (row, index, event) => {
    if (onRowContextMenu) {
      onRowContextMenu(row, index, event);
    }
  };

  /**
   * Handle bulk action
   */
  const handleBulkAction = (action) => {
    const selectedRowsData = filteredAndSortedData.filter(row => selectedRows.has(getRowKey(row)));
    action.onClick(selectedRowsData, selectedRows);
  };

  /**
   * Handle deselect all
   */
  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  /**
   * Page size options
   */
  const pageSizeOptions = [
    { value: 5, display: '5 per page' },
    { value: 10, display: '10 per page' },
    { value: 20, display: '20 per page' },
    { value: 50, display: '50 per page' }
  ];

  /**
   * Handle page size change
   */
  const handlePageSizeChange = (option) => {
    const newPageSize = option.value;
    const currentPageSize = paginationState.pageSize;
    const currentPage = paginationState.currentPage;
    
    // Calculate new page using the formula: k_new = (k * n1) / n2
    // Edge case: if k is 1, k_new is 1
    if (currentPage === 1) {
      setPaginationState({
        currentPage: 1,
        pageSize: newPageSize
      });
      return;
    }

    const newMaxPages = Math.ceil(paginationInfo.totalItems / newPageSize);
    const calculatedNewPage = Math.floor((currentPage * currentPageSize) / newPageSize);
    const newPage = Math.max(1, Math.min(newMaxPages, calculatedNewPage));
    
    setPaginationState({
      currentPage: newPage,
      pageSize: newPageSize
    });
  };

  /**
   * Handle page navigation
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationInfo.totalPages) {
      setPaginationState(prev => ({
        ...prev,
        currentPage: newPage
      }));
    }
  };

  /**
   * Handle column resize start
   */
  const handleResizeStart = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    
    const startX = event.clientX;
    const currentWidth = columnWidths[column.key] ?? DEFAULT_COLUMN_WIDTH_PX;
    
    setResizeState({
      isResizing: true,
      columnKey: column.key,
      startX: startX,
      startWidth: currentWidth,
      currentX: startX
    });
  };

  /**
   * Handle column resize move
   */
  const handleResizeMove = useCallback((event) => {
    if (resizeState.isResizing) {
      setResizeState(prev => ({
        ...prev,
        currentX: event.clientX
      }));
    }
  }, [resizeState.isResizing]);

  /**
   * Handle column resize end
   */
  const handleResizeEnd = useCallback(() => {
    if (resizeState.isResizing) {
      const delta = resizeState.currentX - resizeState.startX;
      const newWidth = Math.max(80, resizeState.startWidth + delta); // Min width 80px
      
      setColumnWidths(prev => ({
        ...prev,
        [resizeState.columnKey]: newWidth
      }));
      
      setResizeState({
        isResizing: false,
        columnKey: null,
        startX: 0,
        startWidth: 0,
        currentX: 0
      });
    }
  }, [resizeState]);

  /**
   * Attach resize event listeners
   */
  useEffect(() => {
    if (resizeState.isResizing) {
      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      
      return () => {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizeState.isResizing, handleResizeMove, handleResizeEnd]);

  /**
   * Page size dropdown state
   */
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const currentPageSizeOption = pageSizeOptions.find(opt => opt.value === paginationState.pageSize) || pageSizeOptions[2]; // Default to 20

  /**
   * Check if all rows across all pages are selected
   */
  const areAllPagesSelected = useMemo(() => {
    if (!Array.isArray(filteredAndSortedData) || filteredAndSortedData.length === 0) return false;
    const allRowKeys = filteredAndSortedData.map(row => getRowKey(row));
    return allRowKeys.every(key => selectedRows.has(key));
  }, [filteredAndSortedData, selectedRows, getRowKey]);

  const totalColumns = processedColumns.length + (enableSelection ? 1 : 0);

  // Render empty state if no data
  if (!Array.isArray(processedData) || processedData.length === 0) {
    return (
      <div className="table-container">
        <div className="table-scroll-wrapper" ref={tableScrollRef}>
          <table 
            className={`sortable-table ${className}`} 
            {...tableProps}
          >
            <thead className="table-header">
              <tr>
                <TableSelectionHeader
                  enableSelection={enableSelection}
                  processedData={processedData}
                  selectedRows={selectedRows}
                  getRowKey={getRowKey}
                  onSelectAll={handleSelectAll}
                />
                {processedColumns.map(column => {
                  const colW = columnWidths[column.key] ?? DEFAULT_COLUMN_WIDTH_PX;
                  return (
                  <th
                    key={column.key}
                    ref={(el) => {
                      filterHeaderRefs.current[column.key] = el;
                    }}
                    className={`
                      sortable-header 
                      ${column.sortable ? 'sortable-header-clickable' : 'sortable-header-non-clickable'}
                      ${column.className}
                    `.trim()}
                    onClick={(event) => handleHeaderClick(column, event)}
                    onContextMenu={(event) => handleHeaderRightClick(event, column)}
                    {...column.headerProps}
                    style={{ 
                      position: 'relative',
                      width: `${colW}px`,
                      minWidth: `${colW}px`
                    }}
                  >
                    <div className="header-content">
                      <div className="header-left">
                        <span className="header-text">{column.title}</span>
                        {getSortIndicator(column)}
                      </div>
                      <div className="header-right">
                        {column.filterable && (
                          <FilterIcon
                            onClick={(event) => handleFilterIconClick(event, column)}
                            hasFilter={hasActiveFilter(column)}
                            hasDisabledFilter={hasDisabledFilter(column)}
                          />
                        )}
                      </div>
                    </div>
                    <div 
                      className="column-resize-handle"
                      onMouseDown={(event) => handleResizeStart(event, column)}
                    />
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="table-body">
              <tr>
                <td colSpan={totalColumns} className="empty-table-message">
                  {emptyMessage || 'No data available'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <TableFilterPopover
          activeFilter={activeFilter}
          processedColumns={processedColumns}
          filterValues={filterValues}
          filterHeaderRefs={filterHeaderRefs}
          onChange={handleFilterChange}
          onClose={closeFilter}
        />
      </div>
    );
  }

  return (
    <div className="table-container">
      <div className="table-scroll-wrapper" ref={tableScrollRef}>
        <table 
          className={`sortable-table ${className}`} 
          {...tableProps}
        >
          <thead className="table-header">
            <tr>
              <TableSelectionHeader
                enableSelection={enableSelection}
                processedData={processedData}
                selectedRows={selectedRows}
                getRowKey={getRowKey}
                onSelectAll={handleSelectAll}
              />
              {processedColumns.map(column => {
                const colW = columnWidths[column.key] ?? DEFAULT_COLUMN_WIDTH_PX;
                return (
                <th
                  key={column.key}
                  ref={(el) => {
                    filterHeaderRefs.current[column.key] = el;
                  }}
                  className={`
                    sortable-header 
                    ${column.sortable ? 'sortable-header-clickable' : 'sortable-header-non-clickable'}
                    ${column.className}
                  `.trim()}
                  onClick={(event) => handleHeaderClick(column, event)}
                  onContextMenu={(event) => handleHeaderRightClick(event, column)}
                  {...column.headerProps}
                  style={{ 
                    position: 'relative',
                    width: `${colW}px`,
                    minWidth: `${colW}px`
                  }}
                >
                  <div className="header-content">
                    <div className="header-left">
                      <span className="header-text">{column.title}</span>
                      {getSortIndicator(column)}
                    </div>
                    <div className="header-right">
                      {column.filterable && (
                        <FilterIcon
                          onClick={(event) => handleFilterIconClick(event, column)}
                          hasFilter={hasActiveFilter(column)}
                          hasDisabledFilter={hasDisabledFilter(column)}
                        />
                      )}
                    </div>
                  </div>
                  <div 
                    className="column-resize-handle"
                    onMouseDown={(event) => handleResizeStart(event, column)}
                  />
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="table-body">
            {processedData.map((row, index) => {
              const rowKey = getRowKey(row, index);
              return (
                <tr
                  key={rowKey}
                  className={`${onRowClick ? 'clickable-row' : ''} ${selectedRows.has(rowKey) ? 'selected-row' : ''} ${highlightedRows.has(rowKey) ? 'highlighted-row' : ''}`}
                  onClick={(event) => handleRowClick(row, index, event)}
                  onContextMenu={(event) => handleRowContextMenu(row, index, event)}
                >
                  <TableSelectionCell
                    enableSelection={enableSelection}
                    row={row}
                    selectedRows={selectedRows}
                    getRowKey={getRowKey}
                    onSelect={handleRowSelection}
                  />
                  {processedColumns.map(column => {
                    const colW = columnWidths[column.key] ?? DEFAULT_COLUMN_WIDTH_PX;
                    return (
                    <td
                      key={`${rowKey}-${column.key}`}
                      className={column.className}
                      {...column.cellProps}
                      style={{
                        width: `${colW}px`,
                        minWidth: `${colW}px`,
                        maxWidth: `${colW}px`
                      }}
                    >
                      {column.renderCell(row, index)}
                    </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <TableFilterPopover
        activeFilter={activeFilter}
        processedColumns={processedColumns}
        filterValues={filterValues}
        filterHeaderRefs={filterHeaderRefs}
        onChange={handleFilterChange}
        onClose={closeFilter}
      />
      
      {/* Column Resize Preview */}
      {resizeState.isResizing && tableScrollRef.current && (() => {
        const tableRect = tableScrollRef.current.getBoundingClientRect();
        const previewX = resizeState.currentX - tableRect.left;
        const newWidth = Math.max(80, resizeState.startWidth + (resizeState.currentX - resizeState.startX));
        
        // Get header height
        const headerRow = tableScrollRef.current.querySelector('.table-header tr');
        const headerHeight = headerRow ? headerRow.offsetHeight : 50; // fallback to 50px
        
        return (
          <div 
            className="column-resize-preview"
            style={{
              left: `${previewX}px`,
              top: 0,
              height: `${headerHeight}px`
            }}
          >
            <div className="column-resize-tooltip">
              {newWidth}px
            </div>
          </div>
        );
      })()}
      
      <TableBulkDock
        enableSelection={enableSelection}
        selectedRows={selectedRows}
        onDeselectAll={handleDeselectAll}
        bulkActions={bulkActions}
        onBulkAction={handleBulkAction}
      />

      <TablePagination
        filteredAndSortedData={filteredAndSortedData}
        paginationInfo={paginationInfo}
        pageSizeOptions={pageSizeOptions}
        pageSizeDropdownOpen={pageSizeDropdownOpen}
        onTogglePageSize={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
        currentPageSizeOption={currentPageSizeOption}
        onPageSizeChange={handlePageSizeChange}
        enableSelection={enableSelection}
        areAllPagesSelected={areAllPagesSelected}
        onSelectAllPages={handleSelectAllPages}
        settingsButton={settingsButton}
        onPageChange={handlePageChange}
      />

      {/* Context Menu */}
      {contextMenu.isOpen && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          column={contextMenu.column}
          sortState={sortState}
          onSort={handleContextSort}
          onFilter={handleContextFilter}
          onClose={closeContextMenu}
          hasActiveFilter={hasActiveFilter}
        />
      )}
    </div>
  );
};

export default Table; 